import type { TableFilterProps } from './TableFilter';

/**
 * Stable key for a filter: its search param, or the `from-to` pair for a date
 * range. Used to key filters in the URL-active check and the collapsible bar's
 * draft set.
 */
export function getFilterKey(props: TableFilterProps): string {
  if ('searchParam' in props && typeof props.searchParam === 'string') {
    return props.searchParam;
  }

  if (
    'fromDateParam' in props &&
    typeof props.fromDateParam === 'string' &&
    'toDateParam' in props &&
    typeof props.toDateParam === 'string'
  ) {
    return `${props.fromDateParam}-${props.toDateParam}`;
  }

  console.warn('A filter in TableFilters is missing a key-able param.', props);
  return JSON.stringify(props);
}

/**
 * Whether the collapsible bar should show this filter rather than tuck it behind
 * "Add filter". NOT "has the user filtered by this" — a filter that never renders
 * an unset state (see below) reports true even at its untouched default, since
 * there's no "unset" look for it to collapse into.
 *
 * The rule is "hide if empty", not "hide if default": a filter can define its own
 * `isActive` (required for `custom` filters, whose URL footprint isn't inferable).
 * Otherwise:
 * - a `select` without a `placeholder`/`clearable` escape hatch, or a `toggleGroup`,
 *   always shows a real selection (the URL value, or its default) — never empty.
 * - a `multiSelect` with a non-empty `defaultValue` shows that default when the URL
 *   holds no value for it — also never empty (the cleared sentinel written when the
 *   user empties it is a real, non-empty URL value, so it's already covered by the
 *   generic check below).
 * - a `date` with a `getDefaultValue` shows that day when the URL holds none.
 * - a `checkbox` with a non-empty `defaultValue` is checked when the URL holds no
 *   value for it, and its uncheck writes an explicit 'false' — either way a real
 *   state, never an empty one.
 * Every other variant — and either of the above once the URL value is empty but no
 * default fills it — is active iff its param is present with a non-empty value (the
 * store removes a filter's param when it's cleared, so presence means a real value).
 */
export function shouldShowFilter(props: TableFilterProps, searchParams: URLSearchParams): boolean {
  if (props.isActive) return props.isActive(searchParams);

  if (props.variant === 'dateRange') {
    return searchParams.has(props.fromDateParam) || searchParams.has(props.toDateParam);
  }

  if (props.variant === 'date' && props.getDefaultValue) return true;
  if (props.variant === 'select' && !props.placeholder && !props.clearable) return true;
  if (props.variant === 'multiSelect' && props.defaultValue && props.defaultValue.length > 0) return true;
  if (props.variant === 'checkbox' && props.defaultValue && props.defaultValue.length > 0) return true;
  if (props.variant === undefined || props.variant === 'toggleGroup') return true;

  // Every remaining variant carries a `searchParam` (injected by toFilterColumns).
  const searchParam = 'searchParam' in props ? props.searchParam : undefined;
  if (typeof searchParam !== 'string') return false;

  const value = searchParams.get(searchParam);
  return value !== null && value !== '';
}
